import React, { useEffect, useState } from "react";
import { fetchSegmentStreamByCallUniqueId, type SegmentStreamRecord } from "@/lib/axios/api/call-quality.api";
import { useDashboard } from "@/contexts/DashboardContext";
import { CQD_COLORS_LIGHT, CQD_COLORS_DARK } from "./CallQualityDistributionWidget";
import type { CallQualityStatus } from "./CallQualityDetailWidget";
import { cn } from "@/lib/utils";
import { convertLocalTimezone } from "@/utils/dateUtils";

interface CallQualityStreamTimelineProps {
  /** Preferred: GET /segment-stream/{callUniqueId} */
  callUniqueId: number | null;
  /** Fallback when callUniqueId is missing (legacy responses) */
  callId: string;
}

function getQualityBadgeStyle(
  status: CallQualityStatus,
  theme: "light" | "dark"
): { backgroundColor: string; color: string } {
  const colors = theme === "light" ? CQD_COLORS_LIGHT : CQD_COLORS_DARK;
  const darkText = "#0C3451";
  const whiteText = "#fff";
  switch (status) {
    case "Good":
      return { backgroundColor: colors[0], color: whiteText };
    case "Poor":
    case "Bad":
      return { backgroundColor: colors[1], color: darkText };
    case "Unclassified":
    default:
      return { backgroundColor: colors[2], color: whiteText };
  }
}

function getSegmentQuality(segment: SegmentStreamRecord): CallQualityStatus {
  const qualities = (segment.streams ?? []).map((s) => String(s.quality ?? "").toLowerCase());
  if (qualities.includes("poor") || qualities.includes("bad")) return "Poor";
  if (qualities.includes("good")) return "Good";
  return "Unclassified";
}

function getFailureReasonLabel(reason?: string | null): string | null {
  const normalized = String(reason ?? "").trim();
  if (!normalized) return null;
  if (normalized.toLowerCase() === "none") return null;
  return normalized.length > 0 ? normalized : null;
}

function getSegmentStreamRequestKey(callUniqueId: number | null, callId: string): number | string | null {
  if (callUniqueId != null && Number.isFinite(callUniqueId)) return callUniqueId;
  if (callId) return callId;
  return null;
}

const CallQualityStreamTimeline: React.FC<CallQualityStreamTimelineProps> = ({ callUniqueId, callId }) => {
  const { theme } = useDashboard();
  const [segments, setSegments] = useState<SegmentStreamRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const streamKey = getSegmentStreamRequestKey(callUniqueId, callId);

  useEffect(() => {
    if (streamKey == null || streamKey === "") return;
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    setSegments([]);

    fetchSegmentStreamByCallUniqueId(streamKey, { signal: controller.signal })
      .then((data) => setSegments(data ?? []))
      .catch((err) => {
        if (err?.name !== "AbortError" && err?.name !== "CanceledError") {
          setError("Unable to load segment details.");
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [streamKey]);

  if (loading) {
    return <div className="text-sm text-muted-foreground dark:text-white/70 px-4 py-2">Loading stream timeline...</div>;
  }

  if (error) {
    return <div className="text-sm text-red-600 dark:text-red-400 px-4 py-2">{error}</div>;
  }

  if (segments.length === 0) {
    return null; // Don't show anything if there are no segments
  }

  return (
    <div className="w-full overflow-x-auto pb-4 pt-0 px-4 no-scrollbar">
      <div className="flex items-start min-w-max relative pt-6">
        {/* Continuous horizontal line across all segments */}
        <div
          className="absolute h-[2px] bg-slate-400 dark:bg-slate-500 top-[63px] z-0"
          style={{ left: '100px', width: `${Math.max(0, (segments.length - 1) * 200)}px` }}
        />

        {segments.map((segment, index) => {
          const quality = getSegmentQuality(segment);
          const badgeStyle = getQualityBadgeStyle(quality, theme ?? "light");
          const failureReason = getFailureReasonLabel(segment.failureType);
          const totalSec = Math.max(0, Math.round(typeof segment.duration === "number" ? segment.duration : 0));
          const duration = `${String(Math.floor(totalSec / 3600)).padStart(2, "0")}:${String(Math.floor((totalSec % 3600) / 60)).padStart(2, "0")}:${String(totalSec % 60).padStart(2, "0")}`;
          const callerText = segment.caller || "Unknown";
          const calleeText = segment.callee || "Unknown";
          const startTimeText = segment.startTime ? convertLocalTimezone(segment.startTime) : "";

          return (
            <div key={`segment-${index}`} className="flex flex-col items-center relative z-10 w-[200px] flex-shrink-0">
              {/* Start Time */}
              <div className="h-6 flex items-end mb-2">
                {startTimeText && (
                  <span className="text-[11px] text-slate-500 dark:text-slate-400 font-medium text-center">
                    {startTimeText}
                  </span>
                )}
              </div>

              {/* Circle Node */}
              <div className="w-4 h-4 rounded-full bg-white dark:bg-[#0C3451] border-2 border-blue-500 dark:border-blue-400 mb-4 z-10" />

              {/* Info Container */}
              <div className="flex flex-col items-center text-center space-y-1 px-2">
                <div className="text-xs text-primary dark:text-white font-medium max-w-[180px] break-words line-clamp-2" title={`${callerText} to ${calleeText}`}>
                  {callerText} to {calleeText}
                </div>
                <div className="text-xs text-slate-500 dark:text-slate-400">
                  Duration: {duration}
                </div>

                <div
                  className="mt-1 px-3 py-1 rounded text-xs font-semibold shadow-sm"
                  style={badgeStyle}
                >
                  {quality}
                </div>

                {failureReason && (
                  <div className="mt-1 px-2 py-0.5 rounded text-[10px] font-medium bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 border border-red-200 dark:border-red-800">
                    {failureReason}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CallQualityStreamTimeline;
